import React, { useMemo, useState } from 'react';
import { Table, Segmented, Dropdown, Popconfirm, Button, Grid } from 'antd';
import { getInvitationState, formatExpiry, filterInvitations, formatRelativeTime } from './dashboardUtils';
import './dashboard.css';

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'expired', label: 'Expired' },
  { value: 'accepted', label: 'Accepted' },
];

const roleLabel = (role) => (role ? role.charAt(0).toUpperCase() + role.slice(1) : '—');

export default function InvitationsTable({
  invitations,
  onCopyLink,
  onCopyMessage,
  onRenew,
  onRevoke,
  copiedKey,
}) {
  const screens = Grid.useBreakpoint();
  const isPhone = screens.md === false;
  const all = useMemo(() => invitations || [], [invitations]);
  const [tab, setTab] = useState(null);
  const [pending, setPending] = useState({});
  const [confirmId, setConfirmId] = useState(null);

  const counts = useMemo(() => {
    const c = { all: all.length };
    TABS.slice(1).forEach((t) => {
      c[t.value] = filterInvitations(all, t.value).length;
    });
    return c;
  }, [all]);

  const activeTab = tab || (counts.pending > 0 ? 'pending' : 'all');
  const visible = useMemo(() => filterInvitations(all, activeTab), [all, activeTab]);

  const run = async (id, fn) => {
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await fn();
    } catch (e) {
      // errors are surfaced by the parent
    } finally {
      setPending((p) => ({ ...p, [id]: false }));
    }
  };

  const renderEmail = (inv) => (
    <div className="invitation-email">
      <div className="invitation-email-text">{inv.email}</div>
    </div>
  );

  const renderExpiry = (inv) => {
    const state = getInvitationState(inv);
    if (state === 'accepted') return <span className="invitation-expiry ok">Accepted</span>;
    if (state === 'revoked') return <span className="invitation-expiry danger">Revoked</span>;
    const { text, tone } = formatExpiry(inv.expires_at);
    return <span className={`invitation-expiry ${tone}`}>{text}</span>;
  };

  const renderActions = (inv) => {
    const state = getInvitationState(inv);
    if (state !== 'pending' && state !== 'expired') return null;
    const id = inv.invitation_id;
    const busy = !!pending[id];
    const items = [
      ...(state === 'pending'
        ? [{ key: 'message', label: copiedKey === `${id}-message` ? 'Copied' : 'Copy message' }]
        : []),
      { key: 'renew', label: 'Renew invitation' },
      { key: 'revoke', label: 'Revoke', danger: true },
    ];
    const onMenuClick = ({ key }) => {
      if (key === 'message') return run(id, () => onCopyMessage(inv));
      if (key === 'renew') return run(id, () => onRenew(id));
      return setConfirmId(id);
    };
    return (
      <div className="invitation-actions">
        {state === 'pending' && (
          <Button
            size={isPhone ? 'middle' : 'small'}
            type="primary"
            ghost
            className="invitation-btn"
            disabled={busy}
            onClick={() => run(id, () => onCopyLink(inv))}
          >
            {copiedKey === `${id}-link` ? 'Copied' : 'Copy link'}
          </Button>
        )}
        <Popconfirm
          title={`Revoke invitation for ${inv.email}?`}
          description="The invitation link will stop working."
          okText="Revoke"
          okButtonProps={{ danger: true }}
          open={confirmId === id}
          onOpenChange={(open) => !open && setConfirmId(null)}
          onConfirm={() => {
            setConfirmId(null);
            return run(id, () => onRevoke(id));
          }}
          onCancel={() => setConfirmId(null)}
        >
          <Dropdown trigger={['click']} menu={{ items, onClick: onMenuClick }}>
            <Button
              size={isPhone ? 'middle' : 'small'}
              className="invitation-btn"
              loading={busy}
              aria-label={`More actions for ${inv.email}`}
            >
              •••
            </Button>
          </Dropdown>
        </Popconfirm>
      </div>
    );
  };

  const columns = [
    { title: 'Email', key: 'email', render: (_, inv) => renderEmail(inv) },
    { title: 'Role', key: 'role', render: (_, inv) => roleLabel(inv.role) },
    { title: 'Invited by', key: 'invited_by', render: (_, inv) => inv.invited_by_name || '—' },
    { title: 'Sent', key: 'sent', render: (_, inv) => formatRelativeTime(inv.created_at) },
    { title: 'Expires', key: 'expires', render: (_, inv) => renderExpiry(inv) },
    { title: '', key: 'actions', align: 'right', render: (_, inv) => renderActions(inv) },
  ];

  const empty = <div className="members-empty">No invitations in this view</div>;

  return (
    <div className="dashboard-card invitations-tile">
      <div className="members-header">
        <h2>
          Invitations <span className="members-count">{counts.all}</span>
        </h2>
      </div>
      <div className="invitations-tabs">
        <Segmented
          value={activeTab}
          onChange={setTab}
          options={TABS.map((t) => ({
            value: t.value,
            label: (
              <span>
                {t.label} <span className="invitation-tab-count">{counts[t.value]}</span>
              </span>
            ),
          }))}
        />
      </div>

      {isPhone ? (
        <div className="members-cards">
          {visible.length === 0
            ? empty
            : visible.map((inv) => (
                <div className="member-card invitation-card" data-testid="invitation-card" key={inv.invitation_id}>
                  <div className="invitation-card-email">{inv.email}</div>
                  <div className="member-card-meta">
                    <span>{roleLabel(inv.role)}</span>
                    {renderExpiry(inv)}
                    <div className="member-muted">Invited by: {inv.invited_by_name || '—'}</div>
                    <div className="member-muted">Sent: {formatRelativeTime(inv.created_at)}</div>
                  </div>
                  <div className="invitation-card-actions">{renderActions(inv)}</div>
                </div>
              ))}
        </div>
      ) : (
        <Table
          rowKey="invitation_id"
          columns={columns}
          dataSource={visible}
          locale={{ emptyText: empty }}
          pagination={visible.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        />
      )}
    </div>
  );
}
